import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useTheme } from "@/lib/ThemeContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquarePlus, X, Bug, Lightbulb, TrendingUp, MessageCircle, Camera, Loader2, Check } from "lucide-react";

const TYPES = [
  { value: "bug", label: "Bug", icon: Bug, color: "#EF4444", placeholder: "What went wrong?" },
  { value: "feature", label: "Feature", icon: Lightbulb, color: "#3B82F6", placeholder: "What would you like to see?" },
  { value: "improvement", label: "Improve", icon: TrendingUp, color: "#F59E0B", placeholder: "What could be better?" },
  { value: "general", label: "General", icon: MessageCircle, color: "#6B7280", placeholder: "What's on your mind?" },
];

export default function FeedbackWidget() {
  const { user } = useAuth();
  const { tokens: t } = useTheme();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!user) return null;

  const selectedType = TYPES.find(ty => ty.value === type);
  const canSubmit = title.trim().length >= 3 && description.trim().length >= 20;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        type,
        title: title.trim(),
        description: description.trim(),
        page_url: window.location.pathname,
        browser_info: `${navigator.userAgent} | ${window.screen.width}x${window.screen.height}`,
      });
      setSuccess(true);
      toast.success("Thanks for your feedback! Track it on the feedback board.");
      setTimeout(() => { setOpen(false); setSuccess(false); setTitle(""); setDescription(""); setType("bug"); }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    }
    setSubmitting(false);
  };

  return (
    <>
      {/* Floating button */}
      <button onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 z-[90] w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        style={{ background: "linear-gradient(135deg, #7C3AED, #6366F1)", color: "#fff" }}
        title="Send feedback"
        data-testid="feedback-widget-btn">
        <MessageSquarePlus className="h-5 w-5" />
      </button>

      {/* Slide-over panel */}
      {open && (
        <div className="fixed inset-0 z-[100] flex justify-end" data-testid="feedback-panel">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md h-full overflow-y-auto"
            style={{ background: t.bgPrimary, borderLeft: `1px solid ${t.borderColor}` }}>
            <div className="sticky top-0 z-10 px-5 py-4 flex items-center justify-between"
              style={{ background: `${t.bgPrimary}ee`, borderBottom: `1px solid ${t.borderColor}` }}>
              <h2 className="text-sm font-bold" style={{ color: t.textPrimary }}>Send Feedback</h2>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10" data-testid="feedback-close">
                <X className="h-4 w-4" style={{ color: t.textMuted }} />
              </button>
            </div>

            {success ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center bg-green-500/20">
                  <Check className="h-8 w-8 text-green-400" />
                </div>
                <span className="text-sm font-medium" style={{ color: t.textPrimary }}>Feedback submitted!</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
                {/* Type selector */}
                <div>
                  <label className="text-xs font-semibold block mb-2" style={{ color: t.textMuted }}>Type</label>
                  <div className="flex gap-2">
                    {TYPES.map(ty => (
                      <button key={ty.value} type="button" onClick={() => setType(ty.value)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all flex-1"
                        style={{
                          background: type === ty.value ? `${ty.color}15` : t.bgSecondary,
                          border: `1px solid ${type === ty.value ? ty.color : t.borderColor}`,
                          color: type === ty.value ? ty.color : t.textMuted,
                        }}
                        data-testid={`feedback-type-${ty.value}`}>
                        <ty.icon className="h-3.5 w-3.5" /> {ty.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: t.textMuted }}>Title</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100}
                    className="w-full px-3 py-2 text-sm rounded-lg outline-none"
                    style={{ background: t.bgSecondary, border: `1px solid ${t.borderColor}`, color: t.textPrimary }}
                    placeholder={selectedType?.placeholder || "Summary"}
                    data-testid="feedback-title" />
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: t.textMuted }}>Description</label>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5}
                    className="w-full px-3 py-2 text-sm rounded-lg outline-none resize-none"
                    style={{ background: t.bgSecondary, border: `1px solid ${t.borderColor}`, color: t.textPrimary }}
                    placeholder="Describe in detail... (min 20 characters)"
                    data-testid="feedback-description" />
                  <span className="text-[9px] mt-0.5 block" style={{ color: description.length >= 20 ? t.success : t.textMuted }}>
                    {description.length}/20 min
                  </span>
                </div>

                <button type="submit" disabled={!canSubmit || submitting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-40"
                  style={{ background: selectedType?.color || t.accent, color: "#fff" }}
                  data-testid="feedback-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />}
                  {submitting ? "Submitting..." : "Submit feedback"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
